import {
  Abi,
  Address,
  ContractFunctionName as ViemContractFunctionName,
  erc20Abi,
} from "viem"

import useAllowanceERC20Token from "../erc-20/useAllowanceERC20Token"

import useEstimateTransactionGas from "./useEstimateTransactionGas"

export default function useEstimateTransactionGasWithApprove<
  const ContractAbi extends Abi | readonly unknown[],
  ContractFunctionName extends
    | ViemContractFunctionName<ContractAbi>
    | undefined = undefined,
>(
  approveTxOptions: {
    tokenContractAddress: Address
    amountToSpent: bigint
    spender: Address
  },
  ...args: Parameters<
    typeof useEstimateTransactionGas<ContractAbi, ContractFunctionName>
  >
) {
  const [contract, options] = args

  const { data: approvedAmount, isFetching: isFetchingApprovedAmount } =
    useAllowanceERC20Token(
      approveTxOptions.tokenContractAddress,
      approveTxOptions.spender,
      options.chainId,
    )

  const {
    estimatedTransactionFee: estimatedApproveTransactionFee,
    isFetching: isFetchingApproveTransactionFee,
    gas: approveGas,
  } = useEstimateTransactionGas(
    {
      abi: erc20Abi,
      functionName: "approve",
      args: [approveTxOptions.spender, approveTxOptions.amountToSpent],
      address: approveTxOptions.tokenContractAddress,
    },
    {
      chainId: options.chainId,
      query: {
        enabled:
          approvedAmount !== undefined &&
          approvedAmount < approveTxOptions.amountToSpent,
      },
    },
  )

  const {
    estimatedTransactionFee: estimatedContractTransactionFee,
    isFetching: isFetchingContractTransactionFee,
    gasPrice,
    gas: transactionGas,
    // @ts-expect-error TODO how to fix this error.
  } = useEstimateTransactionGas(contract, options)

  const estimatedTransactionFee =
    estimatedApproveTransactionFee + estimatedContractTransactionFee

  const gasAmount = (approveGas ?? 0n) + (transactionGas ?? 0n)

  return {
    isFetching:
      isFetchingContractTransactionFee ||
      isFetchingApproveTransactionFee ||
      isFetchingApprovedAmount,
    gas: gasAmount,
    gasPrice,
    estimatedTransactionFee,
  }
}
